import {
  ChatBubbleLeftEllipsisIcon,
  EllipsisVerticalIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useIsUserManagingAnyCourse } from "../../utils/auth";
import { IconWithTooltip } from "../iconWithTooltip";
import { openModal } from "../modal";

export interface MenuItem {
  id: string;
  icon: ReactNode;
  label: string;
  onClick: () => void;
  visible: boolean;
  priority: number;
}

interface ResponsiveMenuProps {
  className?: string;
}

export default function ResponsiveMenu({
  className = "",
}: ResponsiveMenuProps) {
  const { t } = useTranslation(["header"]);
  const isManagingCourse = useIsUserManagingAnyCourse();
  const feedbackForumURL = "https://groups.google.com/a/onetutor.ai/g/feedback";

  const menuItems: MenuItem[] = [
    {
      id: "feedback",
      icon: <ChatBubbleLeftEllipsisIcon className="h-5 w-5" />,
      label: "Feedback",
      onClick: () => openModal("feedbackFormModal"),
      visible: true,
      priority: 3,
    },
    {
      id: "documentation",
      icon: <QuestionMarkCircleIcon className="h-5 w-5" />,
      label: t("documentation"),
      onClick: () => window.open(t("docURL"), "_blank", "noopener,noreferrer"),
      visible: isManagingCourse,
      priority: 2,
    },
    {
      id: "feedbackForum",
      icon: <UserGroupIcon className="h-5 w-5" />,
      label: t("feedbackForumURL"),
      onClick: () =>
        window.open(feedbackForumURL, "_blank", "noopener,noreferrer"),
      visible: isManagingCourse,
      priority: 1,
    },
  ];

  const visibleItems = menuItems
    .filter((item) => item.visible)
    .sort((a, b) => b.priority - a.priority);

  return (
    <div className={`flex items-center ${className}`}>
      <div className="hidden xl:flex xl:items-center xl:gap-1">
        {visibleItems.map((item) => (
          <IconWithTooltip
            key={item.id}
            icon={item.icon}
            tooltip={item.label}
            handler={item.onClick}
            className="btn btn-ghost btn-sm btn-circle"
            tooltipPosition="bottom"
          />
        ))}
      </div>

      <div className="hidden lg:flex lg:items-center lg:gap-1 xl:hidden">
        {visibleItems
          .filter((item) => item.priority >= 3)
          .map((item) => (
            <IconWithTooltip
              key={item.id}
              icon={item.icon}
              tooltip={item.label}
              handler={item.onClick}
              className="btn btn-ghost btn-sm btn-circle"
              tooltipPosition="bottom"
            />
          ))}

        {visibleItems.filter((item) => item.priority < 3).length > 0 && (
          <div className="dropdown dropdown-hover dropdown-end">
            <div className="rounded-full">
              <button
                tabIndex={0}
                type="button"
                className="btn btn-ghost btn-sm btn-circle"
              >
                <EllipsisVerticalIcon className="h-5 w-5" />
              </button>
            </div>
            <ul className="dropdown-content menu z-[1] w-52 gap-1 rounded-box bg-neutral p-2 shadow">
              {visibleItems
                .filter((item) => item.priority < 3)
                .map((item) => (
                  <li key={item.id}>
                    <button type="button" tabIndex={0} onClick={item.onClick}>
                      {item.icon}
                      <span>{item.label}</span>
                    </button>
                  </li>
                ))}
            </ul>
          </div>
        )}
      </div>

      <div className="dropdown dropdown-hover dropdown-end lg:hidden">
        <div className="rounded-full">
          <button
            tabIndex={0}
            type="button"
            className="btn btn-ghost btn-sm btn-circle"
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </button>
        </div>
        <ul className="dropdown-content menu z-[1] w-64 gap-1 rounded-box bg-neutral p-2 shadow">
          {visibleItems.map((item) => (
            <li key={item.id}>
              <button type="button" tabIndex={0} onClick={item.onClick}>
                {item.icon}
                <span>{item.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
